import { Link } from "react-router-dom";
import { formatIstDateTime } from "../utils/datetime";

const COPY = {
  processing: {
    kicker: "Hold on",
    title: "Confirming your payment",
    body: "We are talking to the bank. Keep this page open — your booking is created only after the payment is confirmed.",
  },
  pending: {
    kicker: "Still with the bank",
    title: "Payment is processing",
    body: "The gateway has not confirmed this payment yet. If money was deducted, wait a few minutes and check My Bookings. Do not pay again.",
  },
  success: {
    kicker: "You're booked",
    title: "Thank you",
    body: "Payment received. Here is what we confirmed for you.",
  },
  failed: {
    kicker: "Not charged",
    title: "Payment did not go through",
    body: "The booking is not confirmed. You can try again with the same details, or pick another payment method.",
  },
  cancelled: {
    kicker: "Closed",
    title: "Payment was cancelled",
    body: "You closed the payment window before it finished. Nothing was booked.",
  },
  expired: {
    kicker: "Session ended",
    title: "We lost the booking details",
    body: "The payment tab came back, but this browser no longer has the booking form. If money was deducted, write to us with the payment reference below.",
  },
  paid_unconfirmed: {
    kicker: "Payment received",
    title: "Booking is not confirmed yet",
    body: "The payment went through, but we could not save the booking. Do not pay again. Share the payment reference with support and we will complete it.",
  },
  uncertain: {
    kicker: "Checking",
    title: "We could not confirm this payment",
    body: "The bank did not return a payment reference. If money was deducted, wait a few minutes and check your bookings. Do not pay again.",
  },
  unknown: {
    kicker: "Something is off",
    title: "This payment link is incomplete",
    body: "We did not receive a success or failure from the gateway. Go home and try the booking again if nothing appears in My Bookings.",
  },
};

function Stamp({ kind }) {
  const map = {
    success: "Confirmed",
    processing: "Working",
    pending: "Processing",
    failed: "Declined",
    cancelled: "Cancelled",
    expired: "Expired",
    paid_unconfirmed: "Paid",
    uncertain: "Pending",
    unknown: "Unknown",
  };
  return <span className={`pay-stamp pay-stamp--${kind}`}>{map[kind] || "Status"}</span>;
}

export default function PaymentStatusView({
  kind,
  rows = [],
  cta,
  supportEmail,
  supportPhone,
  createdAt,
  message,
  typeLabel,
}) {
  const copy = { ...(COPY[kind] || COPY.unknown) };
  if (kind === "success" && typeLabel && /wallet/i.test(typeLabel)) {
    copy.kicker = "Balance updated";
    copy.body = "The amount is in your RapidMediK wallet.";
  }
  if (kind === "success" && typeLabel && /pay via app/i.test(typeLabel) && !/appointment|lab|order|home/i.test(typeLabel)) {
    copy.kicker = "Paid";
    copy.body = "This Pay via App bill is confirmed. Show the transaction to the partner if needed.";
  }
  const isPositive = kind === "success";
  const isWait = kind === "processing" || kind === "pending" || kind === "uncertain";
  const showRetry = kind === "failed" || kind === "cancelled" || kind === "unknown" || kind === "expired";

  return (
    <section className={`pay-result pay-result--${kind}`}>
      <div className="pay-result__wash" aria-hidden="true" />
      <article className="pay-slip">
        <header className="pay-slip__head">
          <p className="pay-slip__brand">RapidMediK</p>
          <Stamp kind={kind} />
        </header>

        {isWait ? <div className="pay-pulse" aria-hidden="true"><span /><span /></div> : null}

        <p className="pay-slip__kicker">{copy.kicker}</p>
        <h1 className="pay-slip__title">{copy.title}</h1>
        <p className="pay-slip__lede">{copy.body}</p>
        {message ? <p className="pay-slip__note">{message}</p> : null}

        {rows.length > 0 ? (
          <dl className="pay-rows">
            {rows.map((row) => (
              <div key={`${row.label}-${row.value}`} className={row.emphasize ? "pay-rows__item is-total" : "pay-rows__item"}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <p className="pay-slip__when">
          {createdAt ? formatIstDateTime(createdAt) : formatIstDateTime(new Date())} · IST
        </p>

        <div className="pay-actions">
          {isPositive && cta?.href ? (
            <Link className="pay-btn pay-btn--solid" to={cta.href}>{cta.label || "Continue"}</Link>
          ) : null}
          {kind === "paid_unconfirmed" && cta?.href ? (
            <Link className="pay-btn pay-btn--solid" to={cta.href}>{cta.label || "Check bookings"}</Link>
          ) : null}
          {showRetry && cta?.retry ? (
            <Link className="pay-btn pay-btn--solid" to={cta.retry}>Try again</Link>
          ) : null}
          {isWait ? (
            <button className="pay-btn pay-btn--solid" type="button" onClick={() => window.location.reload()}>
              Check again
            </button>
          ) : null}
          <Link className="pay-btn pay-btn--ghost" to="/">Back home</Link>
        </div>

        {(supportEmail || supportPhone) && (kind === "paid_unconfirmed" || kind === "expired" || kind === "uncertain" || kind === "pending") ? (
          <p className="pay-slip__help">
            Need help?{" "}
            {supportPhone ? <a href={`tel:${supportPhone}`}>{supportPhone}</a> : null}
            {supportPhone && supportEmail ? " · " : null}
            {supportEmail ? <a href={`mailto:${supportEmail}`}>{supportEmail}</a> : null}
          </p>
        ) : null}
      </article>
    </section>
  );
}
